import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Info, ExternalLink, ChevronRight, ChevronDown, Clock,
  CheckCircle2, BookOpen
} from 'lucide-react';
import CHANNEL_GUIDES from '../data/channelGuides';

const CHANNEL_COLORS = {
  instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2',
  whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B', heygen: '#D4AF37'
};

export function ChannelGuideDialog({ channel, children }) {
  const { i18n } = useTranslation();
  const [expandedStep, setExpandedStep] = useState(null);
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const guide = CHANNEL_GUIDES[channel];

  if (!guide) return children || null;

  const steps = guide.steps[lang] || guide.steps.en;
  const title = guide.title[lang] || guide.title.en;

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children || (
          <button
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors p-1 rounded-md hover:bg-[hsl(var(--primary))]/10"
            data-testid={`guide-trigger-${channel}`}
            title={lang === 'tr' ? 'Entegrasyon Rehberi' : 'Integration Guide'}
          >
            <BookOpen size={16} />
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[hsl(var(--card))] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${CHANNEL_COLORS[channel]}20` }}
            >
              <BookOpen size={18} style={{ color: CHANNEL_COLORS[channel] }} />
            </div>
            <div>
              <span className="text-lg">{title}</span>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-[10px] border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))]">
                  <Clock size={10} className="mr-1" />
                  {guide.timeEstimate}
                </Badge>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Developer Portal Link */}
          <a
            href={guide.devUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/5 hover:bg-[hsl(var(--primary))]/10 transition-colors group"
            data-testid={`guide-devlink-${channel}`}
          >
            <ExternalLink size={18} className="text-[hsl(var(--primary))]" />
            <div className="flex-1">
              <p className="text-sm font-medium">{guide.devLabel}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{guide.devUrl}</p>
            </div>
            <ChevronRight size={16} className="text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] transition-colors" />
          </a>

          {/* Steps */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              {lang === 'tr' ? 'Adımlar' : 'Steps'}
            </h3>
            {steps.map((step, idx) => (
              <button
                key={idx}
                onClick={() => setExpandedStep(expandedStep === idx ? null : idx)}
                className="w-full text-left"
                data-testid={`guide-step-${channel}-${idx}`}
              >
                <div className={`p-3 rounded-xl border transition-all duration-200 ${
                  expandedStep === idx
                    ? 'border-white/15 bg-white/5'
                    : 'border-white/5 hover:border-white/10 hover:bg-white/[0.02]'
                }`}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                      style={{
                        backgroundColor: `${CHANNEL_COLORS[channel]}15`,
                        color: CHANNEL_COLORS[channel]
                      }}
                    >
                      {idx + 1}
                    </div>
                    <span className="text-sm font-medium flex-1">{step.title}</span>
                    {expandedStep === idx
                      ? <ChevronDown size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
                      : <ChevronRight size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />}
                  </div>
                  {expandedStep === idx && (
                    <div className="mt-3 ml-10 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed border-l-2 pl-3"
                      style={{ borderLeftColor: `${CHANNEL_COLORS[channel]}40` }}>
                      {step.desc}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Tips */}
          <div className="p-3 rounded-xl border border-[hsl(var(--accent))]/20 bg-[hsl(var(--accent))]/5">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
              <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                {lang === 'tr'
                  ? 'Bilgileri girdikten sonra "Bağlantıyı Test Et" butonuna tıklayarak bağlantınızın doğru çalıştığından emin olun.'
                  : 'After entering credentials, click "Test Connection" to verify everything works correctly.'}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FieldHelpTooltip({ channel, fieldKey }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const guide = CHANNEL_GUIDES[channel];

  if (!guide || !guide.fieldHelp || !guide.fieldHelp[fieldKey]) return null;

  const helpText = guide.fieldHelp[fieldKey][lang] || guide.fieldHelp[fieldKey].en;

  return (
    <span
      className="inline-flex items-center ml-1 cursor-help"
      title={helpText}
      data-testid={`field-help-${channel}-${fieldKey}`}
    >
      <Info size={13} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors" />
    </span>
  );
}
